import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Drop-in replacement for `useState` that persists state to `sessionStorage`.
 *
 * State is restored when the component remounts (e.g. user navigates back to
 * a page), so filter/search/tab state is NOT lost on navigation.
 *
 * State is stored per browser tab (sessionStorage), so:
 * - Multiple tabs are isolated from each other ✅
 * - Closing the tab clears the state ✅
 * - Refreshing the page keeps the state ✅
 *
 * @param {string} storageKey  Unique key, e.g. 'page_filters_personnel'
 * @param {*}      defaultValue  Same as the initial value you'd pass to useState
 * @returns {[*, Function]}  [state, setState] — identical API to useState
 *
 * @example
 *   // Before:
 *   const [tabQueryState, setTabQueryState] = useState(() => buildDefault());
 *   // After:
 *   const [tabQueryState, setTabQueryState] = usePersistentPageState('page_filters_personnel', () => buildDefault());
 */
export function usePersistentPageState(storageKey, defaultValue) {
  const [state, setStateInternal] = useState(() => {
    const defaultVal = typeof defaultValue === "function" ? defaultValue() : defaultValue;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw !== null) {
        const parsed = JSON.parse(raw);
        if (
          parsed &&
          typeof parsed === "object" &&
          defaultVal &&
          typeof defaultVal === "object" &&
          !Array.isArray(parsed) &&
          !Array.isArray(defaultVal)
        ) {
          return { ...defaultVal, ...parsed };
        }
        return parsed;
      }
    } catch (_) {
      // Corrupted data — fall through to default
    }
    return defaultVal;
  });

  // Use a ref to always have the latest state in the write effect without
  // adding `state` as a dependency (which would cause extra renders).
  const stateRef = useRef(state);
  stateRef.current = state;

  // Persist whenever state changes.
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(stateRef.current));
    } catch (_) {
      // Quota exceeded or private mode — silently ignore.
    }
  }, [storageKey, state]);

  // Wrap setState so it has the same functional-update signature as React's.
  const setState = useCallback(
    (updater) => {
      setStateInternal((prev) => {
        const next =
          typeof updater === "function" ? updater(prev) : updater;
        return next;
      });
    },
    []
  );

  return [state, setState];
}

/**
 * Clears all persisted page-filter keys from sessionStorage.
 * Call this on logout so one user's filters don't leak to the next.
 */
export const ALL_PAGE_FILTER_KEYS = [
  // Personnel
  "page_filters_personnel",
  "page_tab_personnel",
  // Clients
  "page_filters_clients",
  "page_tab_clients",
  // Jobs
  "page_tab_jobs",
  "page_search_jobs",
  "page_shifts_filters_jobs",
  "page_client_loc_tab_jobs",
  "page_staffing_sort_by_jobs",
  "page_staffing_sort_direction_jobs",
  "page_staffing_from_date_jobs",
  "page_staffing_to_date_jobs",
  "page_staffing_show_deleted_jobs",
  "page_shift_assignment_filters_jobs",
  "page_ts_status_jobs",
  "page_ts_client_jobs",
  // Schedule
  "page_filters_schedule",
  "page_search_schedule",
  // Accounting
  "page_filters_accounting",
  // Admin Accounts
  "page_tab_admin_accounts",
  "page_search_admin_accounts",
  "page_filters_admin_accounts",
  // Admin Settings
  "page_tab_settings",
  "page_search_settings",
  "page_division_filters_settings",
  "page_show_deleted_settings",
  "page_signin_statuses_settings",
  "page_skills_filter_settings",
  "page_positions_filter_settings",
  "page_divisions_filter_settings",
  "page_offices_filter_settings",
  "page_app_notes_filter_settings",
  // Admin Messages
  "page_tab_messages",
  "page_search_messages",
  "page_queue_filter_messages",
  "page_mq_type_messages",
  "page_dates_messages",
  // Admin Timesheets
  "page_search_admin_timesheets",
  "page_status_admin_timesheets",
  "page_client_admin_timesheets",
];

export function clearAllPageFilterState() {
  for (const key of ALL_PAGE_FILTER_KEYS) {
    try {
      sessionStorage.removeItem(key);
    } catch (_) {
      // ignore
    }
  }
}

export default usePersistentPageState;
